import type {
  EntityId,
  HumanCommandSource,
  SquadArchetype,
} from '@signalclash/shared';
import {
  abilityForArchetype,
  isOperational,
  pickSlotAt,
  secondaryAbilityForArchetype,
} from '@signalclash/shared';
import Phaser from 'phaser';

import type { ClientApp } from '../game/ClientApp.js';
import type { SettingsStore } from '../save/Settings.js';
import { showTapPulse } from '../ui/TapPulse.js';

import type { BuildMode } from './BuildMode.js';
import {
  BUILDING_HIT_RADIUS,
  BUILDING_HIT_RADIUS_TOUCH,
  CONTROL_NODE_HIT_RADIUS_TOUCH,
  CORE_HIT_RADIUS,
  CORE_HIT_RADIUS_TOUCH,
  SQUAD_HIT_RADIUS,
  SQUAD_HIT_RADIUS_TOUCH,
  pickBuilding,
  pickControlNode,
  pickCore,
  pickEnemySquad,
  pickSquad,
} from './Pickers.js';
import type { ArmedMode, TouchTrack } from './Pickers.js';
import { hapticTap, pointerEventIsTouch } from './Profile.js';
import type { Selection } from './Selection.js';
import { ensureBuildContextSelected } from './buildContext.js';

export type { ArmedMode } from './Pickers.js';

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 2.0;
const ZOOM_STEP = 0.0015;
// Two-finger pinch zoom: each unit of finger-distance delta maps to this much
// multiplicative zoom. Tuned so a "comfortable spread" gesture (~150 px on a
// modern phone) walks from ZOOM_MIN to roughly 1.5× and back.
const PINCH_ZOOM_STEP = 0.005;
// Tap classifier: a primary-finger pointerdown→pointerup within this distance
// and time is treated as a tap (selection / move / ability target). Anything
// longer/farther becomes a no-op (drag was a pan attempt that fell back to
// single-finger, which we choose to ignore rather than fire a stray order).
const TAP_MAX_MOVE_PX = 12;
const TAP_MAX_MS = 350;

type Mode = 'idle' | 'panning' | 'boxSelect';

// Box-select drag threshold — pixels of movement after pointer-down before
// the gesture upgrades from "left-click" to "drag-rect". Same threshold as
// touch tap classifier so the gesture vocabulary stays consistent.
const BOX_SELECT_MIN_PX = 8;
// Right-button drag-pan threshold. Slightly larger than BOX_SELECT_MIN_PX so a
// micro-wiggle on a right-click move order doesn't get reinterpreted as a pan.
const RIGHT_DRAG_PAN_PX = 12;
// Control-group slots — bound to digit keys 1..NUM_CONTROL_GROUPS.
const NUM_CONTROL_GROUPS = 6;

// Translates DOM/Phaser events into intents. NEVER moves a sprite directly — the only
// way movement happens is by emitting a Command via HumanCommandSource. Camera
// pan/zoom is the lone exception (presentation concern with no sim impact).
//
// V0.4 adds Build mode: 'B' toggles build (defaulting to Production Hub if not
// already armed); when armed, left-click on the canvas picks the nearest Core slot
// and emits a BuildCommand. ESC cancels build mode (and any picker arming).
//
// V1 adds keyboard panning (WASD / arrow keys). Each frame InputRouter samples
// key state via Phaser keyboard and pans the camera by `scrollSpeedPxPerSec`
// from SettingsStore — divided by camera zoom so the *visible* travel rate is
// constant regardless of zoom level.
//
// V1.7 dropped the player-facing explicit Attack-Move (A) and Stop (S) verbs.
// Combat auto-engages anything in range whether the order is Move or
// attack_move (see CombatSystem.acquireAndFireSquads), so the "Attack" button
// added little over a plain Move that walks past unless halt-to-fight matters
// — and the A/S keys were colliding with the V1.6 WASD camera pan. The sim's
// attack_move command stays (it's how a touch tap on an enemy resolves and
// how the bot pushes), but it is no longer exposed on the player surface.
// Camera pan now uses A/S unconditionally.
export class InputRouter {
  private readonly scene: Phaser.Scene;
  private readonly app: ClientApp;
  private readonly selection: Selection;
  private readonly human: HumanCommandSource;
  private readonly buildMode: BuildMode;
  private readonly settings: SettingsStore;
  private readonly getZoomMin: () => number;
  private mode: Mode = 'idle';
  private spaceHeld = false;
  // Mark Target requires a click target. Q on a Recon arms this; the next
  // left-click on an enemy squad emits the AbilityCommand.
  private markArmedSquadId: EntityId | null = null;
  // V1.5+ Slice 2 — Cut Link arming. E on a Recon arms this; the next
  // left-click on a relay emits the AbilityCommand with hotkey 'E'.
  private cutLinkArmedSquadId: EntityId | null = null;
  private dragLastX = 0;
  private dragLastY = 0;
  private worldCursor = { x: 0, y: 0 };
  // Touch state — a per-touch-id table so multi-finger pinch/pan reliably
  // identifies the right pointer. Phaser delivers each touch as its own
  // pointer object, with `pointer.id` stable across move/up.
  private touchPointers = new Map<number, TouchTrack>();
  // Tap-classifier metadata for the current primary touch. When >1 touch
  // arrives we switch into pinch/pan mode and forget the tap-candidate.
  private primaryTapStartX = 0;
  private primaryTapStartY = 0;
  private primaryTapStartT = 0;
  private primaryTapId: number | null = null;
  private pinchActive = false;
  private pinchLastDistance = 0;
  private pinchLastMidX = 0;
  private pinchLastMidY = 0;
  // Single-finger pan. Activated when one touch exceeds TAP_MAX_MOVE_PX with
  // no second finger present. This is safe because box-select-drag is not
  // implemented for touch, so there is no conflicting intent to protect.
  // Tracks the last screen position so each move event contributes an
  // incremental pan delta (same arithmetic as two-finger midpoint pan).
  private singleFingerPanActive = false;
  private singleFingerLastX = 0;
  private singleFingerLastY = 0;
  // Phaser keys for keyboard pan. Arrow keys + WASD — all unconditional. Held
  // state is sampled in updatePan() each scene tick (not on key-down events)
  // so diagonal motion combines cleanly and pan-speed doesn't depend on input
  // event cadence. V1.7 dropped the squad-selection gate on A/S that V1.6
  // added to dodge the attack-move/stop keybindings — those verbs no longer
  // exist on the player surface so the conflict is gone.
  private keyUp: Phaser.Input.Keyboard.Key | null = null;
  private keyDown: Phaser.Input.Keyboard.Key | null = null;
  private keyLeft: Phaser.Input.Keyboard.Key | null = null;
  private keyRight: Phaser.Input.Keyboard.Key | null = null;
  private keyW: Phaser.Input.Keyboard.Key | null = null;
  private keyA: Phaser.Input.Keyboard.Key | null = null;
  private keyS: Phaser.Input.Keyboard.Key | null = null;
  private keyD: Phaser.Input.Keyboard.Key | null = null;
  private lastPanMs = 0;
  // Right-button drag-pan: pointer-down records start position; on pointer-move
  // past RIGHT_DRAG_PAN_PX the right-click becomes a pan gesture. The move-order
  // is fired on pointer-up only if the drag stayed below the threshold (so a
  // quick right-click still issues a move).
  private rightDragStartX = 0;
  private rightDragStartY = 0;
  private rightDragLastX = 0;
  private rightDragLastY = 0;
  private rightDragActive = false;
  private rightDragPanned = false;
  // Left-click pending flag: set on pointer-down, cleared on commit (click or
  // box-select). Replaces the previous "boxStartScreenX !== 0" sentinel which
  // silently failed on a click at exact pixel 0.
  private leftClickPending = false;
  // Box-select drag state. `boxStartScreenX/Y` is the pointer-down position
  // (screen px) which we use to draw the screen-space rectangle overlay.
  // `boxStartWorldX/Y` is the cached world coordinate so on release we can
  // walk every own squad in the rectangle. `boxOverlayEl` is the DOM div
  // that paints the rectangle; created lazily on first drag.
  private boxStartScreenX = 0;
  private boxStartScreenY = 0;
  private boxStartWorldX = 0;
  private boxStartWorldY = 0;
  private boxLastScreenX = 0;
  private boxLastScreenY = 0;
  private boxOverlayEl: HTMLDivElement | null = null;
  // Control groups: slot -> {primary id, additional ids}. Empty slots
  // are recall-no-ops.
  private controlGroups: Array<EntityId[]> = Array.from({ length: NUM_CONTROL_GROUPS }, () => []);
  private ctrlHeld = false;
  // Digit keys 1..NUM_CONTROL_GROUPS. Bound at init.
  private digitKeys: Array<Phaser.Input.Keyboard.Key | null> = new Array<Phaser.Input.Keyboard.Key | null>(NUM_CONTROL_GROUPS).fill(null);

  constructor(deps: {
    scene: Phaser.Scene;
    app: ClientApp;
    selection: Selection;
    human: HumanCommandSource;
    buildMode: BuildMode;
    settings: SettingsStore;
    getZoomMin?: () => number;
  }) {
    this.scene = deps.scene;
    this.app = deps.app;
    this.selection = deps.selection;
    this.human = deps.human;
    this.buildMode = deps.buildMode;
    this.settings = deps.settings;
    this.getZoomMin = deps.getZoomMin ?? (() => ZOOM_MIN);

    const input = this.scene.input;
    // V3 mobile: ensure Phaser allocates extra pointer slots so two-finger
    // pinch/pan gestures both deliver events. Phaser's default is mouse + 1
    // touch — we need at least 2 active touches concurrently.
    input.addPointer(2);
    input.on(Phaser.Input.Events.POINTER_DOWN, this.onPointerDown);
    input.on(Phaser.Input.Events.POINTER_UP, this.onPointerUp);
    input.on(Phaser.Input.Events.POINTER_MOVE, this.onPointerMove);
    input.on(Phaser.Input.Events.GAME_OUT, this.endPan);
    input.on('wheel', this.onWheel);

    const kb = this.scene.input.keyboard;
    if (kb !== null && kb !== undefined) {
      const Codes = Phaser.Input.Keyboard.KeyCodes;
      kb.addCapture(Codes.SPACE);
      kb.on('keydown-SPACE', this.onSpaceDown);
      kb.on('keyup-SPACE', this.onSpaceUp);
      kb.on('keydown-B', this.onBuildDown);
      kb.on('keydown-Q', this.onAbilityDown);
      kb.on('keydown-E', this.onSecondaryAbilityDown);
      kb.on('keydown-ESC', this.onEscapeDown);
      // Modifier tracking for Ctrl+1..6 (bind) vs 1..6 (recall).
      kb.on('keydown-CTRL', this.onCtrlDown);
      kb.on('keyup-CTRL', this.onCtrlUp);
      this.keyUp = kb.addKey(Codes.UP);
      this.keyDown = kb.addKey(Codes.DOWN);
      this.keyLeft = kb.addKey(Codes.LEFT);
      this.keyRight = kb.addKey(Codes.RIGHT);
      this.keyW = kb.addKey(Codes.W);
      this.keyA = kb.addKey(Codes.A);
      this.keyS = kb.addKey(Codes.S);
      this.keyD = kb.addKey(Codes.D);
      const digitCodes = [Codes.ONE, Codes.TWO, Codes.THREE, Codes.FOUR, Codes.FIVE, Codes.SIX];
      for (let i = 0; i < NUM_CONTROL_GROUPS; i++) {
        this.digitKeys[i] = kb.addKey(digitCodes[i]!);
      }
      kb.on('keydown-ONE', () => this.handleControlGroupKey(0));
      kb.on('keydown-TWO', () => this.handleControlGroupKey(1));
      kb.on('keydown-THREE', () => this.handleControlGroupKey(2));
      kb.on('keydown-FOUR', () => this.handleControlGroupKey(3));
      kb.on('keydown-FIVE', () => this.handleControlGroupKey(4));
      kb.on('keydown-SIX', () => this.handleControlGroupKey(5));
    }
    this.scene.events.on(Phaser.Scenes.Events.UPDATE, this.onSceneUpdate);
  }

  dispose(): void {
    const input = this.scene.input;
    input.off(Phaser.Input.Events.POINTER_DOWN, this.onPointerDown);
    input.off(Phaser.Input.Events.POINTER_UP, this.onPointerUp);
    input.off(Phaser.Input.Events.POINTER_MOVE, this.onPointerMove);
    input.off(Phaser.Input.Events.GAME_OUT, this.endPan);
    input.off('wheel', this.onWheel);
    const kb = this.scene.input.keyboard;
    kb?.off('keydown-SPACE', this.onSpaceDown);
    kb?.off('keyup-SPACE', this.onSpaceUp);
    kb?.off('keydown-B', this.onBuildDown);
    kb?.off('keydown-Q', this.onAbilityDown);
    kb?.off('keydown-E', this.onSecondaryAbilityDown);
    kb?.off('keydown-ESC', this.onEscapeDown);
    kb?.off('keydown-CTRL', this.onCtrlDown);
    kb?.off('keyup-CTRL', this.onCtrlUp);
    this.scene.events.off(Phaser.Scenes.Events.UPDATE, this.onSceneUpdate);
    if (this.boxOverlayEl !== null) {
      this.boxOverlayEl.remove();
      this.boxOverlayEl = null;
    }
  }

  // ───── Control groups ──────────────────────────────────────────────────────
  // Ctrl+digit binds the current squad selection (single or group) to a slot;
  // digit alone recalls that slot. Empty slots no-op.
  private readonly onCtrlDown = (): void => {
    this.ctrlHeld = true;
  };

  private readonly onCtrlUp = (): void => {
    this.ctrlHeld = false;
  };

  private handleControlGroupKey(slot: number): void {
    if (slot < 0 || slot >= NUM_CONTROL_GROUPS) return;
    if (this.ctrlHeld) {
      // Bind current squad selection. Non-squad selections clear the slot.
      const ids = this.selection.selectedSquadIds();
      this.controlGroups[slot] = [...ids];
      this.app.sfx.playUiClick();
      return;
    }
    const ids = this.controlGroups[slot] ?? [];
    if (ids.length === 0) {
      this.app.sfx.playUiError();
      return;
    }
    // Filter out squads that no longer exist before re-selecting.
    const state = this.app.getLatestSnapshot();
    const live = state === null ? ids : ids.filter((id) => state.squads[id] !== undefined);
    if (live.length === 0) {
      this.controlGroups[slot] = [];
      this.app.sfx.playUiError();
      return;
    }
    this.selection.selectSquadGroup(live);
    this.controlGroups[slot] = live;
    // Selection-listener inside ClientApp will fire the voice cue for the
    // primary squad's archetype on the resulting subscribe() notification,
    // so we don't need to pass an archetype here (and shouldn't — that path
    // already routes through `viewerSquadArchetype`).
    this.app.sfx.playSelection('squad');
  }

  getWorldCursor(): { x: number; y: number } {
    return this.worldCursor;
  }

  // Robust touch-detection that works across both modern (PointerEvent) and
  // older iOS/Android (TouchEvent) browsers.  PointerEvent always carries
  // pointerType='touch'; TouchEvent has no pointerType, but Phaser sets
  // pointer.wasTouch=true for any event that originated from a touch.
  private isTouchPointer(pointer: Phaser.Input.Pointer): boolean {
    return (
      pointerEventIsTouch(pointer.event as PointerEvent | null) || pointer.wasTouch
    );
  }

  // V5 mobile UX pass — visual confirmation pulse for touch taps that
  // committed a command. On desktop the cursor is the confirmation; on
  // touch the finger covers the destination, so a brief on-screen ring
  // is the only feedback that the tap registered. No-op on non-touch
  // events so desktop is unchanged.
  private pulseTap(
    pointer: Phaser.Input.Pointer,
    touch: boolean,
    kind: 'move' | 'attack' | 'select',
  ): void {
    if (!touch) return;
    showTapPulse(this.scene.game.canvas, pointer.x, pointer.y, kind);
  }

  private readonly onSceneUpdate = (timeMs: number): void => {
    this.updatePan(timeMs);
  };

  private updatePan(timeMs: number): void {
    if (this.lastPanMs === 0) {
      this.lastPanMs = timeMs;
      return;
    }
    const dt = Math.max(0, Math.min(0.1, (timeMs - this.lastPanMs) / 1000));
    this.lastPanMs = timeMs;
    if (this.mode === 'panning') return; // active drag overrides keyboard pan
    let dx = 0;
    let dy = 0;
    // V1.7 — WASD pan is unconditional. A and S used to gate on
    // !squadSelected to avoid colliding with the explicit Attack-Move / Stop
    // keybindings; those verbs were removed from the player surface in V1.7
    // so the gate is no longer needed (squads still auto-engage anything in
    // range — see CombatSystem — and Stop is replaced by issuing a new Move).
    if (this.keyUp?.isDown === true || this.keyW?.isDown === true) dy -= 1;
    if (this.keyDown?.isDown === true || this.keyS?.isDown === true) dy += 1;
    if (this.keyLeft?.isDown === true || this.keyA?.isDown === true) dx -= 1;
    if (this.keyRight?.isDown === true || this.keyD?.isDown === true) dx += 1;
    // V1.6 — edge-pan removed. Inside the CrazyGames iframe the cursor often
    // sits near the viewport edge between gestures and the auto-scroll was
    // more confusing than useful. WASD / arrows / middle-drag / right-drag
    // cover camera movement now.
    if (dx === 0 && dy === 0) return;
    // Diagonal motion: normalize so two-axis pan isn't sqrt(2)× faster.
    const len = Math.hypot(dx, dy);
    dx /= len;
    dy /= len;
    const cam = this.scene.cameras.main;
    const speed = this.settings.get().scrollSpeedPxPerSec;
    // Divide by zoom so the *world* delta is smaller when zoomed in — this
    // matches the click-drag behavior and keeps the visible scroll rate
    // perceptually constant.
    const stepX = (dx * speed * dt) / cam.zoom;
    const stepY = (dy * speed * dt) / cam.zoom;
    cam.scrollX += stepX;
    cam.scrollY += stepY;
  }

  private readonly onSpaceDown = (): void => {
    this.spaceHeld = true;
  };

  private readonly onSpaceUp = (): void => {
    this.spaceHeld = false;
    if (this.mode === 'panning') this.endPan();
  };

  private readonly onBuildDown = (): void => {
    this.buildMode.toggleDefault();
    this.markArmedSquadId = null;
    this.cutLinkArmedSquadId = null;
    // On touch devices, the player's Core may be off-screen when they tap
    // the Build button. Auto-center the camera on their Core so the build
    // slots are immediately visible without requiring a manual pan. Only
    // fires when build mode just became armed (not when cancelling).
    if (this.buildMode.isArmed()) {
      this.app.sfx.playUiBuildOpen();
      // V1.8 UI redesign — the BuildPanel is now CONTEXTUAL: it appears
      // only when the player's own Core / operational Hub is selected.
      // Pressing B without such a selection would arm a verb with no
      // visible buttons, so we also auto-select the player's own Core (or
      // operational Hub) here. This preserves the legacy "B opens build"
      // muscle memory while the new contextual rule is in force.
      ensureBuildContextSelected(
        this.app.getLatestSnapshot(),
        this.selection,
        this.human.playerId,
      );
      this.centerCameraOnOwnCore();
    } else {
      this.app.sfx.playUiClick();
    }
  };

  // Q: fire selected own squad's ability. Recon (Mark Target) needs a click
  // target so we arm a one-shot pick mode; the other three abilities resolve
  // immediately as squad-anchored effects.
  private readonly onAbilityDown = (): void => {
    const sel = this.selection.getCurrent();
    if (sel === null || sel.kind !== 'squad') return;
    const state = this.app.getLatestSnapshot();
    if (state === null) return;
    const squad = state.squads[sel.id];
    if (squad === undefined) return;
    if (squad.ownerId !== this.human.playerId) return;
    if (squad.abilityCooldown > 0) return;
    if (squad.channelingAbility !== null) return;
    const def = abilityForArchetype(squad.archetype);
    if (def.requiresTarget) {
      this.markArmedSquadId = squad.id;
      this.cutLinkArmedSquadId = null;
      this.buildMode.cancel();
      return;
    }
    this.human.requestAbility(squad.id, null, 'Q');
  };

  // V1.5+ Slice 2 — E: fire selected own squad's secondary ability. Currently
  // only Recon has one (Cut Link), and it requires a relay target → arm a
  // one-shot picker that resolves on the next left-click on a control_node.
  private readonly onSecondaryAbilityDown = (): void => {
    const sel = this.selection.getCurrent();
    if (sel === null || sel.kind !== 'squad') return;
    const state = this.app.getLatestSnapshot();
    if (state === null) return;
    const squad = state.squads[sel.id];
    if (squad === undefined) return;
    if (squad.ownerId !== this.human.playerId) return;
    const def = secondaryAbilityForArchetype(squad.archetype);
    if (def === null) return;
    if (squad.secondaryAbilityCooldown > 0) return;
    if (squad.channelingAbility !== null) return;
    if (def.requiresTarget) {
      this.cutLinkArmedSquadId = squad.id;
      this.markArmedSquadId = null;
      this.buildMode.cancel();
      return;
    }
    this.human.requestAbility(squad.id, null, 'E');
  };

  private readonly onEscapeDown = (): void => {
    this.markArmedSquadId = null;
    this.cutLinkArmedSquadId = null;
    this.buildMode.cancel();
  };

  private readonly onPointerDown = (pointer: Phaser.Input.Pointer): void => {
    // V3 mobile: touch / pen events route through a separate state machine so
    // multi-finger pinch+pan and tap-classification stay clean. Mouse events
    // keep the desktop right-click / space-drag flow.
    if (this.isTouchPointer(pointer)) {
      this.onTouchDown(pointer);
      return;
    }
    if (pointer.middleButtonDown() || (pointer.leftButtonDown() && this.spaceHeld)) {
      this.beginPan(pointer);
      return;
    }
    if (pointer.rightButtonDown()) {
      this.buildMode.cancel();
      // Defer the move-order to pointer-up so we can distinguish a quick
      // right-click (issue move) from a right-button drag (pan camera).
      this.rightDragActive = true;
      this.rightDragPanned = false;
      this.rightDragStartX = pointer.x;
      this.rightDragStartY = pointer.y;
      this.rightDragLastX = pointer.x;
      this.rightDragLastY = pointer.y;
      return;
    }
    if (pointer.leftButtonDown()) {
      // Defer the primary action to pointer-up so we can distinguish a click
      // from a box-select drag. Armed verbs (build / mark / cut-link) skip
      // the drag interpretation — those are click-only verbs and shouldn't
      // be hijacked by a tiny mouse wiggle.
      if (this.isArmedAction()) {
        this.dispatchPrimaryClick(pointer);
        return;
      }
      this.boxStartScreenX = pointer.x;
      this.boxStartScreenY = pointer.y;
      this.boxLastScreenX = pointer.x;
      this.boxLastScreenY = pointer.y;
      const world = this.scene.cameras.main.getWorldPoint(pointer.x, pointer.y);
      this.boxStartWorldX = world.x;
      this.boxStartWorldY = world.y;
      this.mode = 'idle';
      this.leftClickPending = true;
    }
  };

  private isArmedAction(): boolean {
    return (
      this.markArmedSquadId !== null ||
      this.cutLinkArmedSquadId !== null ||
      this.buildMode.isArmed()
    );
  }

  // Common primary-button click resolver. Shared by mouse left-click and the
  // touch tap classifier so the armed/build/attack-move/normal-click branches
  // are defined in exactly one place.
  private dispatchPrimaryClick(pointer: Phaser.Input.Pointer): void {
    const touch = this.isTouchPointer(pointer);
    if (this.markArmedSquadId !== null) {
      this.handleMarkClick(pointer, touch);
      this.markArmedSquadId = null;
      return;
    }
    if (this.cutLinkArmedSquadId !== null) {
      this.handleCutLinkClick(pointer, touch);
      this.cutLinkArmedSquadId = null;
      return;
    }
    if (this.buildMode.isArmed()) {
      this.handleBuildClick(pointer);
      return;
    }
    this.handleLeftClick(pointer, touch);
  }

  private readonly onPointerUp = (pointer: Phaser.Input.Pointer): void => {
    if (this.isTouchPointer(pointer)) {
      this.onTouchUp(pointer);
      return;
    }
    if (this.mode === 'panning') {
      if (!pointer.middleButtonDown() && !pointer.leftButtonDown()) this.endPan();
      return;
    }
    // Right-button-up: if the gesture stayed below the pan threshold it was a
    // click → fire the move order on the click position. If it became a pan,
    // swallow it (no order).
    if (this.rightDragActive && !pointer.rightButtonDown()) {
      const wasPan = this.rightDragPanned;
      this.rightDragActive = false;
      this.rightDragPanned = false;
      if (!wasPan) {
        this.handleRightClick(pointer);
      }
      return;
    }
    if (this.mode === 'boxSelect') {
      this.commitBoxSelect(pointer);
      this.mode = 'idle';
      this.removeBoxOverlay();
      return;
    }
    // Deferred primary click: if a pointer-down was recorded for left-button
    // but the pointer never moved past the drag threshold, treat it as a
    // click and dispatch the primary action now.
    if (!pointer.leftButtonDown() && this.leftClickPending) {
      this.dispatchPrimaryClick(pointer);
      this.leftClickPending = false;
    }
  };

  private readonly onPointerMove = (pointer: Phaser.Input.Pointer): void => {
    const world = this.scene.cameras.main.getWorldPoint(pointer.x, pointer.y);
    this.worldCursor.x = world.x;
    this.worldCursor.y = world.y;
    if (this.isTouchPointer(pointer)) {
      this.onTouchMove(pointer);
      return;
    }
    if (this.mode === 'panning') {
      const dx = pointer.x - this.dragLastX;
      const dy = pointer.y - this.dragLastY;
      this.dragLastX = pointer.x;
      this.dragLastY = pointer.y;
      const cam = this.scene.cameras.main;
      cam.scrollX -= dx / cam.zoom;
      cam.scrollY -= dy / cam.zoom;
      return;
    }
    // Right-button drag-pan. Once the cursor moves past RIGHT_DRAG_PAN_PX the
    // gesture is committed to pan; subsequent move events scroll the camera.
    // Captures pointer.rightButtonDown() so a player releasing right early
    // doesn't keep panning on subsequent leftover moves.
    if (this.rightDragActive && pointer.rightButtonDown()) {
      const totalDx = pointer.x - this.rightDragStartX;
      const totalDy = pointer.y - this.rightDragStartY;
      if (!this.rightDragPanned && Math.hypot(totalDx, totalDy) >= RIGHT_DRAG_PAN_PX) {
        this.rightDragPanned = true;
      }
      if (this.rightDragPanned) {
        const cam = this.scene.cameras.main;
        const dx = pointer.x - this.rightDragLastX;
        const dy = pointer.y - this.rightDragLastY;
        cam.scrollX -= dx / cam.zoom;
        cam.scrollY -= dy / cam.zoom;
      }
      this.rightDragLastX = pointer.x;
      this.rightDragLastY = pointer.y;
      return;
    }
    // Left-button drag past the threshold upgrades to a box-select. Don't
    // start the box if the gesture wasn't initiated with left-button-down
    // (leftClickPending is cleared on click commit / box commit).
    if (pointer.leftButtonDown() && !this.spaceHeld && this.leftClickPending) {
      const dragDist = Math.hypot(pointer.x - this.boxStartScreenX, pointer.y - this.boxStartScreenY);
      if (this.mode === 'idle' && dragDist >= BOX_SELECT_MIN_PX) {
        this.mode = 'boxSelect';
        this.ensureBoxOverlay();
      }
      if (this.mode === 'boxSelect') {
        this.boxLastScreenX = pointer.x;
        this.boxLastScreenY = pointer.y;
        this.updateBoxOverlay();
      }
    }
  };

  // ───── Box select ──────────────────────────────────────────────────────────
  private ensureBoxOverlay(): void {
    if (this.boxOverlayEl !== null) return;
    const el = document.createElement('div');
    el.style.cssText = `
      position: fixed;
      border: 1.5px solid #f5e76e;
      background: rgba(245, 231, 110, 0.12);
      box-shadow: 0 0 12px rgba(245, 231, 110, 0.45);
      pointer-events: none;
      z-index: 28;
    `;
    document.body.appendChild(el);
    this.boxOverlayEl = el;
  }

  private updateBoxOverlay(): void {
    if (this.boxOverlayEl === null) return;
    const cssLeft = this.canvasOffsetX() + Math.min(this.boxStartScreenX, this.boxLastScreenX);
    const cssTop = this.canvasOffsetY() + Math.min(this.boxStartScreenY, this.boxLastScreenY);
    const cssW = Math.abs(this.boxLastScreenX - this.boxStartScreenX);
    const cssH = Math.abs(this.boxLastScreenY - this.boxStartScreenY);
    this.boxOverlayEl.style.left = `${cssLeft}px`;
    this.boxOverlayEl.style.top = `${cssTop}px`;
    this.boxOverlayEl.style.width = `${cssW}px`;
    this.boxOverlayEl.style.height = `${cssH}px`;
  }

  private removeBoxOverlay(): void {
    if (this.boxOverlayEl === null) return;
    this.boxOverlayEl.remove();
    this.boxOverlayEl = null;
  }

  private canvasOffsetX(): number {
    const canvas = this.scene.game.canvas;
    if (canvas === undefined) return 0;
    return canvas.getBoundingClientRect().left;
  }

  private canvasOffsetY(): number {
    const canvas = this.scene.game.canvas;
    if (canvas === undefined) return 0;
    return canvas.getBoundingClientRect().top;
  }

  private commitBoxSelect(pointer: Phaser.Input.Pointer): void {
    const state = this.app.getLatestSnapshot();
    if (state === null) return;
    const camStart = this.scene.cameras.main.getWorldPoint(this.boxStartScreenX, this.boxStartScreenY);
    const camEnd = this.scene.cameras.main.getWorldPoint(pointer.x, pointer.y);
    const minX = Math.min(camStart.x, camEnd.x);
    const maxX = Math.max(camStart.x, camEnd.x);
    const minY = Math.min(camStart.y, camEnd.y);
    const maxY = Math.max(camStart.y, camEnd.y);
    const owner = this.human.playerId;
    const inBox: EntityId[] = [];
    for (const s of Object.values(state.squads)) {
      if (s.ownerId !== owner) continue;
      if (s.pos.x < minX || s.pos.x > maxX) continue;
      if (s.pos.y < minY || s.pos.y > maxY) continue;
      inBox.push(s.id);
    }
    if (inBox.length === 0) {
      // Empty drag — treat as a deselect (matches RTS convention).
      this.selection.clear();
      this.leftClickPending = false;
      return;
    }
    this.selection.selectSquadGroup(inBox);
    this.app.sfx.playSelection('squad');
    this.leftClickPending = false;
  }

  // ───── Touch state machine ────────────────────────────────────────────────
  //
  // Single-finger:
  //   - down: record tap candidate (id, world coords, time)
  //   - move: if movement exceeds TAP_MAX_MOVE_PX → upgrade to single-finger pan
  //   - up:   if still a tap candidate (small movement, short time) → fire the
  //           primary-click action (select / move / attack-move / armed click)
  //
  // Multi-finger (2nd pointer down while a touch is active):
  //   - clears the tap candidate (so the lift doesn't fire a stray click)
  //   - enters pinch mode: midpoint pans the camera, distance change zooms.
  //
  // This is the gesture set the deferred-mobile doc plans for V3+ (tap-to-
  // select, two-finger camera, long-press for radial menu — long-press lives
  // in MobileActionBar / ability arming, not here).
  private onTouchDown(pointer: Phaser.Input.Pointer): void {
    const id = pointer.id;
    const t = pointer.downTime;
    this.touchPointers.set(id, {
      id,
      startX: pointer.x,
      startY: pointer.y,
      lastX: pointer.x,
      lastY: pointer.y,
      startT: t,
      moved: false,
    });
    if (this.touchPointers.size === 1) {
      // Primary finger — tap candidate.
      this.primaryTapId = id;
      this.primaryTapStartX = pointer.x;
      this.primaryTapStartY = pointer.y;
      this.primaryTapStartT = t;
      this.mode = 'idle';
      this.singleFingerPanActive = false;
    } else if (this.touchPointers.size >= 2) {
      // Second finger — switch to pinch/pan. Cancel any single-finger pan
      // already in progress so the midpoint takes over cleanly.
      this.primaryTapId = null;
      this.singleFingerPanActive = false;
      const pair = this.firstTwoTouches();
      if (pair !== null) {
        this.pinchActive = true;
        this.pinchLastDistance = Math.hypot(pair.a.lastX - pair.b.lastX, pair.a.lastY - pair.b.lastY);
        this.pinchLastMidX = (pair.a.lastX + pair.b.lastX) / 2;
        this.pinchLastMidY = (pair.a.lastY + pair.b.lastY) / 2;
      }
    }
  }

  private onTouchMove(pointer: Phaser.Input.Pointer): void {
    const track = this.touchPointers.get(pointer.id);
    if (track === undefined) return;
    const prevX = track.lastX;
    const prevY = track.lastY;
    track.lastX = pointer.x;
    track.lastY = pointer.y;
    const totalDx = pointer.x - track.startX;
    const totalDy = pointer.y - track.startY;
    if (!track.moved && Math.hypot(totalDx, totalDy) > TAP_MAX_MOVE_PX) {
      track.moved = true;
      // Single-finger movement past the tap threshold: cancel the tap
      // candidate and start a one-finger pan. There is no box-select on
      // touch, so this is safe — the only prior concern was "selection-drag
      // intent", which never shipped for touch.
      if (this.primaryTapId === pointer.id) {
        this.primaryTapId = null;
        if (this.touchPointers.size === 1) {
          this.singleFingerPanActive = true;
          this.singleFingerLastX = prevX;
          this.singleFingerLastY = prevY;
        }
      }
    }
    // Single-finger pan: apply per-move delta to the camera.
    if (this.singleFingerPanActive && this.touchPointers.size === 1) {
      const dx = pointer.x - this.singleFingerLastX;
      const dy = pointer.y - this.singleFingerLastY;
      this.singleFingerLastX = pointer.x;
      this.singleFingerLastY = pointer.y;
      const cam = this.scene.cameras.main;
      cam.scrollX -= dx / cam.zoom;
      cam.scrollY -= dy / cam.zoom;
    }
    if (!this.pinchActive || this.touchPointers.size < 2) return;
    const pair = this.firstTwoTouches();
    if (pair === null) return;
    const dist = Math.hypot(pair.a.lastX - pair.b.lastX, pair.a.lastY - pair.b.lastY);
    const midX = (pair.a.lastX + pair.b.lastX) / 2;
    const midY = (pair.a.lastY + pair.b.lastY) / 2;
    const cam = this.scene.cameras.main;
    // Pan first (use midpoint delta in screen-px → world-px).
    const panDx = midX - this.pinchLastMidX;
    const panDy = midY - this.pinchLastMidY;
    cam.scrollX -= panDx / cam.zoom;
    cam.scrollY -= panDy / cam.zoom;
    // Then zoom (multiplicative on distance delta).
    const distDelta = dist - this.pinchLastDistance;
    const next = Phaser.Math.Clamp(cam.zoom * (1 + distDelta * PINCH_ZOOM_STEP), this.getZoomMin(), ZOOM_MAX);
    cam.setZoom(next);
    this.pinchLastDistance = dist;
    this.pinchLastMidX = midX;
    this.pinchLastMidY = midY;
  }

  private onTouchUp(pointer: Phaser.Input.Pointer): void {
    const track = this.touchPointers.get(pointer.id);
    this.touchPointers.delete(pointer.id);
    if (this.touchPointers.size < 2) {
      this.pinchActive = false;
    }
    // Single-finger pan ends when the finger lifts.
    if (this.touchPointers.size === 0) {
      this.singleFingerPanActive = false;
    }
    if (track === undefined) return;
    if (this.primaryTapId !== pointer.id) return;
    this.primaryTapId = null;
    const dt = pointer.upTime - this.primaryTapStartT;
    const moved = Math.hypot(pointer.x - this.primaryTapStartX, pointer.y - this.primaryTapStartY);
    if (track.moved || moved > TAP_MAX_MOVE_PX || dt > TAP_MAX_MS) return;
    // Tap! Dispatch via the shared primary-click resolver. Touch path adds an
    // "implicit attack-move on empty ground when a squad is selected" — see
    // handleLeftClick(touch=true).
    this.dispatchPrimaryClick(pointer);
  }

  private firstTwoTouches(): { a: TouchTrack; b: TouchTrack } | null {
    const it = this.touchPointers.values();
    const a = it.next().value as TouchTrack | undefined;
    const b = it.next().value as TouchTrack | undefined;
    if (a === undefined || b === undefined) return null;
    return { a, b };
  }

  private readonly onWheel = (
    _pointer: Phaser.Input.Pointer,
    _objects: Phaser.GameObjects.GameObject[],
    _dx: number,
    dy: number,
  ): void => {
    const cam = this.scene.cameras.main;
    const next = Phaser.Math.Clamp(cam.zoom * (1 - dy * ZOOM_STEP), this.getZoomMin(), ZOOM_MAX);
    cam.setZoom(next);
  };

  private beginPan(pointer: Phaser.Input.Pointer): void {
    this.mode = 'panning';
    this.dragLastX = pointer.x;
    this.dragLastY = pointer.y;
  }

  private readonly endPan = (): void => {
    this.mode = 'idle';
  };

  private handleLeftClick(pointer: Phaser.Input.Pointer, touch = false): void {
    const world = this.scene.cameras.main.getWorldPoint(pointer.x, pointer.y);
    const state = this.app.getLatestSnapshot();
    if (state === null) {
      if (!touch) this.selection.clear();
      return;
    }
    const ownerId = this.human.playerId;
    const radSquad = touch ? SQUAD_HIT_RADIUS_TOUCH : SQUAD_HIT_RADIUS;
    const radBuilding = touch ? BUILDING_HIT_RADIUS_TOUCH : BUILDING_HIT_RADIUS;
    const radCore = touch ? CORE_HIT_RADIUS_TOUCH : CORE_HIT_RADIUS;
    const squadHit = pickSquad(state, world.x, world.y, ownerId, radSquad);
    const buildingHit = squadHit !== null
      ? null
      : pickBuilding(state, world.x, world.y, ownerId, radBuilding);
    const coreHit = squadHit !== null || buildingHit !== null
      ? null
      : pickCore(state, world.x, world.y, ownerId, radCore);
    // Touch path resolves selection-switches BEFORE squad move orders so
    // tapping your own Core/Building while a squad is selected opens that
    // structure (production / build) instead of issuing a doomed move order
    // onto the impassable footprint. Tap-toggle on the already-selected
    // entity, or tap on empty ground while a Core/Building is selected,
    // dismisses — the "tap outside / tap-again-to-close" idiom replaces the
    // explicit ✕ Deselect chip the MobileActionBar used to ship.
    if (touch) {
      const current = this.selection.getCurrent();
      const tapped =
        squadHit !== null ? { kind: 'squad' as const, id: squadHit }
        : buildingHit !== null ? { kind: 'building' as const, id: buildingHit }
        : coreHit !== null ? { kind: 'core' as const, id: coreHit }
        : null;
      if (tapped !== null) {
        // Multi-selecting the primary squad narrows the group to it rather
        // than deselecting everyone — only sole selections toggle off.
        const isToggle =
          current !== null &&
          current.kind === tapped.kind &&
          current.id === tapped.id &&
          (tapped.kind !== 'squad' || this.selection.selectedSquadIds().length === 1);
        if (isToggle) {
          this.selection.clear();
          hapticTap();
          return;
        }
        if (tapped.kind === 'squad') this.selection.selectSquad(tapped.id);
        else if (tapped.kind === 'building') this.selection.selectBuilding(tapped.id);
        else this.selection.selectCore(tapped.id);
        this.pulseTap(pointer, touch, 'select');
        return;
      }
      // Empty ground. A squad selection issues move/attack-move (the mobile
      // contract that lets us play without right-click). Otherwise the tap
      // dismisses any Core/Building selection; nothing-selected stays a
      // no-op (fat-finger fairness — a stray tap shouldn't matter).
      const selected = this.selection.selectedSquadIds();
      if (selected.length > 0) {
        const enemyId = pickEnemySquad(state, world.x, world.y, ownerId, radSquad);
        const enemy = enemyId === null ? undefined : state.squads[enemyId];
        if (enemy !== undefined) {
          this.human.requestAttackMove(selected, { x: enemy.pos.x, y: enemy.pos.y });
          this.app.sfx.playOrderAttack(this.app.resolveOrderArchetype(selected));
          this.pulseTap(pointer, touch, 'attack');
        } else {
          this.human.requestMove(selected, { x: world.x, y: world.y });
          this.app.sfx.playOrderMove(this.app.resolveOrderArchetype(selected));
          this.pulseTap(pointer, touch, 'move');
        }
        hapticTap();
        return;
      }
      if (current !== null && current.kind !== 'squad') {
        this.selection.clear();
        hapticTap();
      }
      return;
    }
    // Desktop path — unchanged. Left-click picks under the cursor; empty
    // click deselects (standard RTS); right-click handles orders.
    if (squadHit !== null) this.selection.selectSquad(squadHit);
    else if (buildingHit !== null) this.selection.selectBuilding(buildingHit);
    else if (coreHit !== null) this.selection.selectCore(coreHit);
    else this.selection.clear();
  }

  private handleRightClick(pointer: Phaser.Input.Pointer): void {
    const ids = this.selection.selectedSquadIds();
    if (ids.length === 0) return;
    const world = this.scene.cameras.main.getWorldPoint(pointer.x, pointer.y);
    this.human.requestMove(ids, { x: world.x, y: world.y });
    this.app.sfx.playOrderMove(this.app.resolveOrderArchetype(ids));
  }

  // Picks the nearest enemy squad under the cursor and emits a Mark Target
  // AbilityCommand. Cancels silently if nothing under the cursor.
  private handleMarkClick(pointer: Phaser.Input.Pointer, touch = false): void {
    const casterId = this.markArmedSquadId;
    if (casterId === null) return;
    const state = this.app.getLatestSnapshot();
    if (state === null) return;
    const world = this.scene.cameras.main.getWorldPoint(pointer.x, pointer.y);
    const rad = touch ? SQUAD_HIT_RADIUS_TOUCH : SQUAD_HIT_RADIUS;
    const enemyId = pickEnemySquad(state, world.x, world.y, this.human.playerId, rad);
    if (enemyId === null) {
      // Click landed on empty ground while Mark was armed — let the player know
      // the cast was a no-op instead of dropping it silently.
      this.app.sfx.playUiError();
      return;
    }
    this.human.requestAbility(casterId, { kind: 'squad', id: enemyId }, 'Q');
    this.app.sfx.playAbilityMark();
    this.pulseTap(pointer, touch, 'attack');
    hapticTap();
  }

  // V1.5+ Slice 2 — picks the nearest relay under the cursor and emits a Cut
  // Link AbilityCommand. The sim re-checks `cutLinkTargetRangePx` on apply so a
  // long-distance click is dropped silently — no need to filter here.
  private handleCutLinkClick(pointer: Phaser.Input.Pointer, touch = false): void {
    const casterId = this.cutLinkArmedSquadId;
    if (casterId === null) return;
    const state = this.app.getLatestSnapshot();
    if (state === null) return;
    const world = this.scene.cameras.main.getWorldPoint(pointer.x, pointer.y);
    const rad = touch ? CONTROL_NODE_HIT_RADIUS_TOUCH : undefined;
    const nodeId = pickControlNode(state, world.x, world.y, rad);
    if (nodeId === null) return;
    this.human.requestAbility(casterId, { kind: 'control_node', id: nodeId }, 'E');
    this.pulseTap(pointer, touch, 'attack');
    hapticTap();
  }

  private handleBuildClick(pointer: Phaser.Input.Pointer): void {
    const kind = this.buildMode.getActive();
    if (kind === null) return;
    const state = this.app.getLatestSnapshot();
    if (state === null) return;
    const world = this.scene.cameras.main.getWorldPoint(pointer.x, pointer.y);
    const slot = pickSlotAt(state, this.human.playerId, world.x, world.y);
    if (slot === null) {
      // Click missed all build slots — error cue so the player knows the click
      // wasn't registered as a place. Build mode stays armed for a second
      // attempt (matches the cancel-only-on-success contract below).
      this.app.sfx.playUiError();
      return;
    }
    this.human.requestBuild(slot.coreId as EntityId, slot.slotIndex, kind);
    // build_start cue lands when the building appears in the next snapshot
    // (diffBuildings) — we don't need an extra UI sound here.
    this.buildMode.cancel();
    this.pulseTap(pointer, this.isTouchPointer(pointer), 'select');
    hapticTap();
  }

  // ───── Public API for the on-screen mobile action bar / armed banner ─────
  //
  // These mirror the keyboard handlers (B, Q, E, ESC) so a single touch on
  // a DOM button has the exact same outcome as a key-press on desktop. Kept
  // public + thin so the action bar doesn't need to reach into private state.
  triggerBuildToggle(): void {
    this.onBuildDown();
  }

  // V5++ — cycle the build-kind on repeated taps (idle → Hub → Turret → idle).
  // The MobileActionBar's Build button uses this so a touch player can pick
  // Hub OR Turret from the same single button without needing to find the
  // Hub/Turret picker inside the floating HUD context panel. Behaviour
  // parallels onBuildDown (clears other armed verbs, centers the camera on
  // own Core when arming the first kind, plays the same SFX).
  triggerBuildCycle(): void {
    const wasArmed = this.buildMode.isArmed();
    this.buildMode.cycleDefault();
    this.markArmedSquadId = null;
    this.cutLinkArmedSquadId = null;
    const nowArmed = this.buildMode.isArmed();
    if (!wasArmed && nowArmed) {
      // First step of the cycle — arming Hub. Center on Core and play the
      // build-open cue so the UX matches the keyboard B path.
      this.app.sfx.playUiBuildOpen();
      ensureBuildContextSelected(
        this.app.getLatestSnapshot(),
        this.selection,
        this.human.playerId,
      );
      this.centerCameraOnOwnCore();
    } else {
      // Cycle within / out of arming — just a click cue, no recenter.
      this.app.sfx.playUiClick();
    }
  }

  triggerPrimaryAbility(): void {
    this.onAbilityDown();
  }

  triggerSecondaryAbility(): void {
    this.onSecondaryAbilityDown();
  }

  triggerCancel(): void {
    this.onEscapeDown();
  }

  // Centers the camera on the player's own Core. Provides a one-tap "go home"
  // shortcut on mobile where there is no Spacebar key and single-finger pan
  // is the only navigation shortcut.
  triggerHome(): void {
    this.centerCameraOnOwnCore();
  }

  // V5+ — produce a squad from the currently-selected own Core or own
  // operational Production Hub. Mirrors the HudPanel production buttons but
  // exposes the action through the same router-trigger pattern the rest of
  // the mobile bar uses. No-op if the selection isn't a producer the player
  // owns (mirrors HudPanel.onProduceClicked's silent-bail).
  triggerProduce(archetype: SquadArchetype): void {
    const sel = this.selection.getCurrent();
    if (sel === null) return;
    if (sel.kind !== 'core' && sel.kind !== 'building') return;
    this.human.requestProduceSquad(sel.id, archetype);
    hapticTap();
  }

  // V5+ — select all of the viewer's own squads. Mobile-RTS equivalent of
  // desktop's box-drag-everything. Fires an error cue if the player has no
  // squads (so the button-press isn't silently dropped). Selection update
  // routes through the same path as a regular click-select, so the
  // command-card / action bar contextual UI auto-updates.
  triggerSelectAll(): void {
    const state = this.app.getLatestSnapshot();
    if (state === null) return;
    const owner = this.human.playerId;
    const ids: EntityId[] = [];
    for (const s of Object.values(state.squads)) {
      if (s.ownerId === owner) ids.push(s.id);
    }
    if (ids.length === 0) {
      this.app.sfx.playUiError();
      return;
    }
    this.selection.selectSquadGroup(ids);
    this.app.sfx.playSelection('squad');
    hapticTap();
  }

  // V5+ — does the current selection point at a producer the player owns and
  // that is currently allowed to produce? Returns the structure kind so
  // callers can render the right enable-state without re-walking the snapshot.
  // Used by MobileActionBar to decide whether to swap its tail buttons (Q/E
  // ability slots) into the four production tiles.
  getSelectedProducer(): { kind: 'core' | 'hub'; producerId: EntityId } | null {
    const sel = this.selection.getCurrent();
    if (sel === null) return null;
    const state = this.app.getLatestSnapshot();
    if (state === null) return null;
    const owner = this.human.playerId;
    if (sel.kind === 'core') {
      const core = state.cores[sel.id];
      if (core === undefined || core.ownerId !== owner) return null;
      // V1.7 (2.12 design fix): the Core always produces — Hubs add parallel
      // queues, they don't take over. So a selected own Core is always a
      // valid producer for the action bar's production tiles.
      return { kind: 'core', producerId: sel.id };
    }
    if (sel.kind === 'building') {
      const b = state.buildings[sel.id];
      if (b === undefined || b.ownerId !== owner) return null;
      if (b.kind !== 'production_hub') return null;
      if (!isOperational(b)) return null;
      return { kind: 'hub', producerId: sel.id };
    }
    return null;
  }

  // Returns one of: 'idle' | 'mark-target' | 'cut-link' | 'build'. Used by
  // the ArmedBanner to show a context-appropriate prompt and a Cancel
  // button. Pure read; no side-effects.
  getArmedMode(): ArmedMode {
    if (this.markArmedSquadId !== null) return 'mark-target';
    if (this.cutLinkArmedSquadId !== null) return 'cut-link';
    if (this.buildMode.isArmed()) return 'build';
    return 'idle';
  }

  // Center the camera on the player's own Core. Used when build mode is
  // armed so mobile players don't have to manually navigate to their Core
  // after tapping the Build button. No-op if the snapshot isn't available
  // yet or the player has no Core (shouldn't happen during normal play).
  private centerCameraOnOwnCore(): void {
    const state = this.app.getLatestSnapshot();
    if (state === null) return;
    const ownerId = this.human.playerId;
    for (const c of Object.values(state.cores)) {
      if (c.ownerId === ownerId) {
        this.scene.cameras.main.pan(c.pos.x, c.pos.y, 300, 'Power2');
        return;
      }
    }
  }
}
